import { AppBar, makeStyles, styled } from "@material-ui/core";
import { useIO, useLinked, useModel } from "@type-r/react";
import { Flex, Loading, Page, SelectSortBy, Toolbar } from "lqx-react";
import React, { useEffect } from "react";
import { RowH1 } from "../../../../../common";
import { ImportJob } from "../api/importJob";
import { ImportJobAssetsDelete } from "../api/importJobAssetsDelete";
import { ImportJobClose } from "../api/importJobClose";
import { ImportJobReprocessor } from "../api/importJobReprocessor";
import { PopupActionDialog } from "../components/popups/popup-action-dialog";
import { AddFilter } from "../components/search-bar/search-bar";
import { ImportJobsList } from "./import-jobs-list";
import { SortingTemplate } from "./models/ImortJobsFilters";
import { ImportJobsStore } from "./models/import-job-store";

export const ImportJobsPage = (props) => {
  const classes = useStyles();
  const state = useModel(ImportJobsStore);
  const $doProcessJob = useLinked<{ jobId?: Number, needApproval?: boolean, actionType: String, assetTypeId: Number }>({ jobId: null, needApproval: null, actionType: '', assetTypeId: null });
  const [importJobs, setImportJobs] = React.useState<ImportJob[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const defaultContext = { onApply: null, onCancel: null, actionType: '', actionText: '', isPopupOpen: false };
  const $popupContext = useLinked<{
    onApply: any,
    onCancel: any,
    actionType: String,
    actionText: String,
    isPopupOpen: boolean
  }>(defaultContext);

  useEffect(() => {
    if (props && props.importJobId) {
      state.selectedFilterBy = 'jobId';
    }
  }, []);

  useIO(async () => {
    if (Boolean($doProcessJob.value.jobId)) {
      if ($doProcessJob.value.needApproval) {
        const actionText = $doProcessJob.value.actionType === 'Reprocess' ?
          `Are you sure you want to ${$doProcessJob.value.actionType} this Import Job? Asset data may be updated.` :
          `Are you sure you want to delete not digitized assets? Asset data may be updated.`
        $popupContext.set({
          onApply: () => onApply(),
          onCancel: () => onCancel(),
          actionType: $doProcessJob.value.actionType,
          actionText: actionText,
          isPopupOpen: true
        });
      } else {
        await doJob(getActionType());
        setImportJobs(state.importJobs.items.models)
      }
    }
  }, [$doProcessJob.value]);

  const getActionType = () => {
    switch ($doProcessJob.value.actionType) {
      case 'Reprocess':
        return state.importJobReprocessor;
      case 'Close':
        return state.importJobClose;
      case 'Delete Assets':
        return state.importJobAssetsDelete;
    }
  }

  const doJob = async (action: ImportJobReprocessor | ImportJobClose | ImportJobAssetsDelete) => {
    try {
      await action.do($doProcessJob.value.jobId, action as ImportJobAssetsDelete && $doProcessJob.value.assetTypeId);
    }
    finally {
      await fetchImportJobs();
      $doProcessJob.set({ jobId: null, needApproval: null, actionType: '', assetTypeId: null });
    }
  }

  const onCancel = () => {
    $doProcessJob.set({ jobId: null, needApproval: null, actionType: '', assetTypeId: null });
    $popupContext.set(defaultContext);
  }

  const onApply = () => {
    $doProcessJob.set({ ...$doProcessJob.value, needApproval: false });
    $popupContext.set(defaultContext);
  }

  const isReady = useIO(async () => {
    await Promise.all([
      state.assetTypes.fetch(),
      state.importJobStatuses.fetch()
    ])
    state.initialize()
    if (state.selectedFilterBy && state.selectedFilterBy == 'jobId') {
      state.importJobsFilter.setJobId(props.importJobId);
    }
    await fetchImportJobs()
    setImportJobs(state.importJobs.items.models)
  }, [])

  const dataLoaded = useIO(async () => {
    if (isReady) {
      await fetchImportJobs()
      setImportJobs(state.importJobs.items.models)
    }
  }, [state.importJobsFilter["_changeToken"]])

  const fetchImportJobs = async (skip = 0) => {
    const request = state.importJobsFilter.mapToQuery(skip);
    setIsLoading(true);
    await state.importJobs.fetch({ params: request });
    setIsLoading(false);
  }

  const scrollEvent = async ({ target }) => {
    const isBottom = target.scrollHeight - target.offsetHeight - target.scrollTop <= 1 && target.scrollHeight / target.offsetHeight > 2.1;
    if (isBottom && !isLoading && state.importJobs.items.models.length === 100) {
      await fetchImportJobs(importJobs.length);
      setImportJobs(prev => [...prev, ...state.importJobs.items.models]);
    }
  }

  return (
    <Page maxWidth={false} header={
      <Flex center>
        <RowH1 className={classes.title}>Import Jobs</RowH1>
      </Flex>
    }
      top={
        <AppBar position="static" color="secondary">
          <Toolbar>
            <StyledFilterBar filter={state.importJobsFilter} filterAllPlaceholder="Filter by asset type, Job state, Job id, filename, created by or import template name" selectedFilterBy={state.selectedFilterBy} />
            <SelectSortBy $attr={state.importJobsFilter.sortBy.$.attr} $asc={state.importJobsFilter.sortBy.$.asc} options={SortingTemplate} />
          </Toolbar>
        </AppBar>
      }
    >
      {isReady && dataLoaded ? <ImportJobsList importJobCards={importJobs} $doProcessJob={$doProcessJob} scrollPagination={scrollEvent}></ImportJobsList> : <Loading />}
      <PopupActionDialog $context={$popupContext} />
    </Page>
  )
};

const useStyles = makeStyles({
  title: {
    padding: '20px 0',
    color: '#3d6889'
  }
});

const StyledFilterBar = styled(AddFilter)({
  width: '650px',
  alignItems: 'center',
  padding: '4px',
}) 
